// src/App.js

import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, useNavigate } from 'react-router-dom';
import { useMempoolWebSocket } from './utils/useMempoolWebSocket';
import { useTimechainWebSocket } from './utils/useTimechainWebSocket';
import { useCoinCapWebSocket } from './utils/useCoinCapWebSocket';
import { useDataOrchestrator } from './utils/data/dataOrchestrator';
import { setupNotifications, notificationUtils } from './utils/notifications';
import { priceAlertManager } from './utils/priceAlerts';
import { GlossaryProvider } from './contexts/GlossaryContext';
import { MasterWrap, AppHeader, AppFooter, AppContent } from './components/layout';
import { Preloader } from './Preloader';
import ScrollToTopButton from './components/decorations/ScrollToTopButton';
import { AIInsightsButton } from './components/decorations/AIInsightsButton';
import ScrollToTop from './components/ScrollToTop';
import { PWAUpdateNotification } from './components/PWAUpdateNotification';
import { useVisibilityChange } from './utils/useVisibilityChange';
import { usePageLifecycle } from './utils/usePageLifecycle';
import { createBackgroundMonitor } from './utils/backgroundReconnect';
import { useRecoilState } from 'recoil';
import { formatNumber } from './utils';
import { memoizedParse, parseBlockData, updateRecentBlocksOptimized, scheduleCacheClear } from './utils/memoryOptimization';
import { fetchPreloaderData } from './utils/diamondbackPreload';
import { preloadFallbackData } from './utils/data/blockchainStatsFallback';
import { recentBlocksState, selectedCurrencyState } from './recoilState';
import { MetricsProvider } from './contexts/MetricsContext';
// import Arcade from './components/games/Arcade';
import CustomDashboard from './components/custom/CustomDashboard';
import { BitcoinCalculatorAdvanced } from './components/data-display/BitcoinCalculatorAdvanced';
import { HelmetProvider } from 'react-helmet-async';
import { RouteSEO } from './components/SEO';
import { registerServiceWorker } from './utils/serviceWorkerRegistration';

const CalculatorRoute = ({ mode, children, data, bitcoinPrice }) => {
  const navigate = useNavigate();
  return (
    <>
      {children}
      <BitcoinCalculatorAdvanced
        isOpen={true}
        onClose={() => navigate('/')}
        data={data}
        bitcoinPrice={bitcoinPrice}
        formatNumber={formatNumber}
        defaultMode={mode}
      />
    </>
  );
};

// Error Boundary to prevent white screen crashes
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Component crash:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#121212',
          color: '#fff',
          padding: '20px',
          textAlign: 'center'
        }}>
          <h1 style={{ color: '#f7931a', marginBottom: '20px' }}>Something went wrong</h1>
          <p style={{ marginBottom: '20px', color: '#999' }}>
            The dashboard encountered an error. Please try reloading the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              backgroundColor: '#f7931a',
              color: '#000',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Price source priority: Coinbase (primary) → Timechain (fallback) → API (last resort)
// If Coinbase goes silent for this duration, unlock so Timechain can take over
const PRICE_SOURCE_TIMEOUT = 10000; // 10 seconds

// Simple flag to enable/disable the notification feature (0 = disabled, 1 = enabled)
const NOTIFICATION_FEATURE_ENABLED = 0;

// Simple flag to enable/disable the AI Insights feature (0 = disabled, 1 = enabled)
const AI_INSIGHTS_FEATURE_ENABLED = 1;

function App() {
  const [apiStatus, setApiStatus] = useState(null);
  const [bitcoinPrice, setBitcoinPrice] = useState(null);

  // Removed glossaryData state - now using GlossaryContext
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  // Background monitor for WebSocket health
  const backgroundMonitorRef = useRef(null);

  // Price source locking — prevents oscillation between Coinbase and Timechain
  const priceSourceRef = useRef(null); // 'coinbase' | 'timechain' | 'api'
  const coinbaseTimeoutRef = useRef(null);

  // API Key for Sidewinder (Left here for others to use - Have fun!)
  const SIDEWINDER_API_KEY = 'EUL9E54951LU3L5FA65M2D9KZQUH7LL05';

  // Initialize notifications on mount
  useEffect(() => {
    if (NOTIFICATION_FEATURE_ENABLED) {
      const initNotifications = async () => {
        const { permission } = await setupNotifications();
        // Check localStorage for user preference
        const storedPreference = localStorage.getItem('blockNotificationsEnabled');
        // Only enable if permission is granted AND user has explicitly enabled
        if (permission === 'granted' && storedPreference === 'true') {
          setNotificationsEnabled(true);
        } else {
          setNotificationsEnabled(false);
        }
      };
      initNotifications();
    } else {
      // Clean up notification-related localStorage items when feature is disabled
      localStorage.removeItem('blockNotificationsEnabled');
      localStorage.removeItem('priceAlerts');
      // Ensure notifications are disabled
      setNotificationsEnabled(false);
    }
  }, []);

  // Register the PWA service worker and keep it updated (independent of notifications)
  useEffect(() => {
    registerServiceWorker();
  }, []);

  // Schedule periodic cache clearing to prevent memory buildup
  useEffect(() => {
    const interval = setInterval(() => {
      scheduleCacheClear();
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);


  // Start price alert monitoring when notifications are enabled
  useEffect(() => {
    if (NOTIFICATION_FEATURE_ENABLED && notificationsEnabled && bitcoinPrice) {
      priceAlertManager.startMonitoring(() => bitcoinPrice, 5000);
    } else {
      priceAlertManager.stopMonitoring();
    }

    return () => {
      priceAlertManager.stopMonitoring();
    };
  }, [notificationsEnabled, bitcoinPrice]);

  // Scroll to top on component mount (page refresh)
  useEffect(() => {
    // Disable browser's scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Force scroll to top immediately
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Also handle window load event
    const handleLoad = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    
    window.addEventListener('load', handleLoad);
    
    // Also try with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 100);
    
    // Clean up on unmount
    return () => {
      window.removeEventListener('load', handleLoad);
      clearTimeout(timeoutId);
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  // Initialize WebSocket hooks
  const { bitcoinPrice: timechainBitcoinPrice } = useTimechainWebSocket();
  const { message: coincapMessage } = useCoinCapWebSocket();
  const mempoolWebSocket = useMempoolWebSocket();
  const { message: mempoolMessage, reconnect: reconnectMempoolWebSocket } = mempoolWebSocket;

  // Initialize event-driven data orchestrator
  const {
    data,
    dataMetadata,
    diamondbackData,
    blockMetrics,
    isLoading,
    refreshOnDemand
    // lastBlockData, blockHistory, blockHistoryStats, refreshEndpoints, refreshDiamondback, pollingStatus
    // Available but not used yet - will be added in Phase 4
  } = useDataOrchestrator({
    mempoolWebSocket,
    apiKey: SIDEWINDER_API_KEY
  });

  const [lastMempoolBlockData, setLastMempoolBlockData] = useState(null);
  const [conversions, setConversions] = useState(null);
  const [da, setDa] = useState(null);
  const [fees, setFees] = useState(null);
  const [mempoolBlocks, setMempoolBlocks] = useState(null);
  const [blocks, setBlocks] = useState(null);
  const [mempoolInfo, setMempoolInfo] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [vBytesPerSecond, setVBytesPerSecond] = useState(null);

  const [recentBlocks, setRecentBlocks] = useRecoilState(recentBlocksState);
  const [selectedCurrency] = useRecoilState(selectedCurrencyState);

  const [animateLastBlock, setAnimateLastBlock] = useState(false);
  const prevBlockHeightRef = useRef(null);

  // Diamondback REST fallback — deferred so WebSockets get a clean network window
  // WebSocket is PRIMARY and delivers in ~500ms. This only fires if WebSocket fails.
  useEffect(() => {
    const fallbackTimer = setTimeout(async () => {
      const preloadData = await fetchPreloaderData();

      // Only set if WebSocket hasn't already provided data
      if (preloadData.mempoolInfo && !mempoolInfo) {
        setMempoolInfo(preloadData.mempoolInfo);
      }
      if (preloadData.fees && !fees) {
        setFees(preloadData.fees);
      }
      if (preloadData.da && !da) {
        setDa(preloadData.da);
      }
      if (preloadData.lastMempoolBlockData && !lastMempoolBlockData) {
        setLastMempoolBlockData(preloadData.lastMempoolBlockData);
        setRecentBlocks([preloadData.lastMempoolBlockData]);
      }
    }, 3000); // 3s delay — WebSocket should deliver by then

    // Blockchain stats fallback — deferred, not needed for preloader
    const fallbackStatsTimer = setTimeout(() => {
      preloadFallbackData();
    }, 5000);

    return () => {
      clearTimeout(fallbackTimer);
      clearTimeout(fallbackStatsTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount - WebSocket will override

  // Set up background monitoring for WebSocket health
  useEffect(() => {
    const checkWebSocketHealth = () => {
      // This will trigger reconnection if needed
      reconnectMempoolWebSocket();
    };

    backgroundMonitorRef.current = createBackgroundMonitor(checkWebSocketHealth);
    backgroundMonitorRef.current.start();

    return () => {
      if (backgroundMonitorRef.current) {
        backgroundMonitorRef.current.stop();
      }
    };
  }, [reconnectMempoolWebSocket]);

  // Listen for service worker messages
  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      if (event.data && event.data.type === 'CHECK_WEBSOCKETS') {
        // Service worker is asking us to check our WebSocket connections
        reconnectMempoolWebSocket();
      }
    };

    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    return () => {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, [reconnectMempoolWebSocket]);

  // Handle page lifecycle events for better background behavior
  usePageLifecycle({
    onFreeze: () => {
      // Browser is suspending the page
      // console.log('Page being frozen');
    },
    onResume: () => {
      // Page resuming from suspended state
      // console.log('Page resuming - reconnecting services');
      reconnectMempoolWebSocket();
      if (refreshOnDemand) {
        refreshOnDemand();
      }
    },
    onHidden: () => {
      // Page is now in background
      // console.log('Page hidden - maintaining connections');
    },
    onVisible: () => {
      // Page is now visible
      // console.log('Page visible - ensuring fresh data');
      if (refreshOnDemand) {
        refreshOnDemand();
      }
    }
  });

  useEffect(() => {
    if (lastMempoolBlockData && (!prevBlockHeightRef.current || prevBlockHeightRef.current < lastMempoolBlockData.height)) {
      setAnimateLastBlock(true);

      setTimeout(() => {
        setAnimateLastBlock(false);
      }, 20000); // Reset after animation duration

      // Trigger notification for new block
      if (notificationsEnabled && prevBlockHeightRef.current !== null && bitcoinPrice) {
        notificationUtils.showNewBlockNotification(lastMempoolBlockData, bitcoinPrice);
      }

      prevBlockHeightRef.current = lastMempoolBlockData.height;
    }
  }, [lastMempoolBlockData, notificationsEnabled, bitcoinPrice]);

  // --- Price source management: true fallback, no oscillation ---
  // Priority: Coinbase (primary) → Timechain (fallback) → API s07 (last resort)
  // Each source is an independent effect. The lock prevents lower-priority
  // sources from overwriting a higher-priority one that's actively delivering.

  // Coinbase — PRIMARY price source
  useEffect(() => {
    if (!coincapMessage) return;
    const myObj = memoizedParse(coincapMessage);
    const num = parseFloat(myObj['bitcoin']);
    if (isNaN(num)) return;

    if (priceSourceRef.current !== 'coinbase') {
      console.log('[Price] Source locked: Coinbase (primary)');
    }
    priceSourceRef.current = 'coinbase';
    setBitcoinPrice(num.toFixed(2));

    // Reset the timeout — if Coinbase goes silent for 10s, unlock for fallback
    clearTimeout(coinbaseTimeoutRef.current);
    coinbaseTimeoutRef.current = setTimeout(() => {
      if (priceSourceRef.current === 'coinbase') {
        priceSourceRef.current = null;
        console.log('[Price] Coinbase timeout — unlocking for fallback');
      }
    }, PRICE_SOURCE_TIMEOUT);
  }, [coincapMessage]);

  // Timechain — FALLBACK price source (only active when Coinbase isn't delivering)
  useEffect(() => {
    if (!timechainBitcoinPrice) return;
    if (priceSourceRef.current === 'coinbase') return; // Coinbase is active, ignore

    if (priceSourceRef.current !== 'timechain') {
      console.log('[Price] Source locked: Timechain (fallback)');
    }
    priceSourceRef.current = 'timechain';
    setBitcoinPrice(timechainBitcoinPrice);
  }, [timechainBitcoinPrice]);

  // API s07 — LAST RESORT (only if no WebSocket has delivered any price)
  useEffect(() => {
    if (data?.s07?.price?.RAW?.BTC?.USD?.PRICE && !bitcoinPrice) {
      priceSourceRef.current = 'api';
      console.log('[Price] Source locked: API s07 (last resort)');
      setBitcoinPrice(data.s07.price.RAW.BTC.USD.PRICE.toFixed(2));
    }
  }, [data, bitcoinPrice]);

  // Cleanup Coinbase timeout on unmount
  useEffect(() => {
    return () => clearTimeout(coinbaseTimeoutRef.current);
  }, []);

  useEffect(() => {
    const hasLatestBlock = (blockHeight) => recentBlocks.find((item) => item.height === blockHeight)
    const updateCondition = lastMempoolBlockData && lastMempoolBlockData.height && !hasLatestBlock(lastMempoolBlockData.height)

    if (updateCondition) {
      const optimizedBlocks = updateRecentBlocksOptimized(recentBlocks, lastMempoolBlockData);
      if (optimizedBlocks !== recentBlocks) {
        setRecentBlocks(optimizedBlocks);
      }
    }
  }, [lastMempoolBlockData, recentBlocks, setRecentBlocks])

  useEffect(() => {
    if (mempoolMessage) {
      const myObj = parseBlockData(mempoolMessage);
      // console.log("Mempool.Space data:", myObj);
      const {
        conversions,
        da,
        fees,
        mempoolInfo,
        transactions,
        vBytesPerSecond,
        block,
        blocks,
      } = myObj

      if (conversions) setConversions(conversions);
      if (da) setDa(da);
      if (fees) setFees(fees);
      if (myObj['mempool-blocks']) setMempoolBlocks(myObj['mempool-blocks']);

      if (blocks) {
        const latestBlock = blocks[blocks.length - 1];

        setBlocks(blocks);
        setRecentBlocks(blocks);
        setLastMempoolBlockData(latestBlock);

        // console.log("Blocks state:", blocks);
      }
      if (mempoolInfo) setMempoolInfo(mempoolInfo);
      if (transactions) setTransactions(transactions);
      if (vBytesPerSecond) setVBytesPerSecond(vBytesPerSecond);

      if (block) {
        setLastMempoolBlockData(block);
      }
    }
  }, [mempoolMessage, setRecentBlocks]);

  useVisibilityChange(() => {
    if (document.visibilityState === "visible") {
      reconnectMempoolWebSocket();

      // Immediately fetch fresh data when app becomes visible
      if (refreshOnDemand) {
        refreshOnDemand();
      }

      // Clear notification badge when app becomes visible
      if ('clearAppBadge' in navigator) {
        navigator.clearAppBadge().catch(() => {
          // Badge clearing not supported or failed
        });
      }
      
      // Also clear all notifications
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.ready.then(registration => {
          registration.getNotifications().then(notifications => {
            notifications.forEach(notification => notification.close());
          });
        });
      }
    }
  });

  // Set API status based on data orchestrator loading state
  useEffect(() => {
    if (!isLoading && data) {
      setApiStatus(true);
    } else if (!isLoading && !data) {
      setApiStatus(false);
    }
  }, [isLoading, data]);

  // Document title (and per-route meta) is now owned by RouteSEO via react-helmet-async

  useEffect(() => {
    return () => {
      // console.log('App component unmounted');
    };
  }, []);

  // Glossary data is now loaded on-demand via GlossaryContext

  useEffect(() => {
    // console.log('Data updated:', data);
  }, [data]);

  // Removed glossary data logging

  return (
    <HelmetProvider>
    <ErrorBoundary>
    <GlossaryProvider>
      <MetricsProvider
        data={data}
        bitcoinPrice={bitcoinPrice}
        lastMempoolBlockData={lastMempoolBlockData}
        recentBlocks={recentBlocks}
        fees={fees}
        mempoolInfo={mempoolInfo}
        da={da}
        diamondbackData={diamondbackData}
        blockMetrics={blockMetrics}
        selectedCurrency={selectedCurrency}
        blocks={blocks}
      >
      <Router>
        <>
          <ScrollToTop />
          <RouteSEO bitcoinPrice={bitcoinPrice} />
        <Preloader
          data={data}
          bitcoinPrice={bitcoinPrice}
          lastMempoolBlockData={lastMempoolBlockData}
          fees={fees}
          mempoolInfo={mempoolInfo}
          da={da}
          formatNumber={formatNumber}
        />
        <MasterWrap>
          <AppHeader
            setApiStatus={setApiStatus}
            bitcoinPrice={bitcoinPrice}
            data={data}
            lastMempoolBlockData={lastMempoolBlockData}
            formatNumber={formatNumber}
            notificationsEnabled={notificationsEnabled}
            setNotificationsEnabled={setNotificationsEnabled}
            notificationFeatureEnabled={NOTIFICATION_FEATURE_ENABLED}
          />
          <Routes>
            {['/', '/network', '/mining', '/market', '/charts', '/resources', '/stonks'].map((path) => (
              <Route key={path} path={path} element={
                  <AppContent
                        data={data}
                    bitcoinPrice={bitcoinPrice}
                    lastMempoolBlockData={lastMempoolBlockData}
                    conversions={conversions}
                    da={da}
                    fees={fees}
                    mempoolBlocks={mempoolBlocks}
                    blocks={blocks}
                    mempoolInfo={mempoolInfo}
                    transactions={transactions}
                    vBytesPerSecond={vBytesPerSecond}
                    recentBlocks={recentBlocks}
                    diamondbackData={diamondbackData}
                    blockMetrics={blockMetrics}
                  />
              } />
            ))}

            {/* <Route path="/arcade" element={
              <Arcade
                bitcoinPrice={bitcoinPrice}
                lastMempoolBlockData={lastMempoolBlockData}
                  />
            } /> */}

            <Route
              path="/donate"
              element={
                  <AppContent
                          data={data}
                    bitcoinPrice={bitcoinPrice}
                    lastMempoolBlockData={lastMempoolBlockData}
                    conversions={conversions}
                    da={da}
                    fees={fees}
                    mempoolBlocks={mempoolBlocks}
                    blocks={blocks}
                    mempoolInfo={mempoolInfo}
                    transactions={transactions}
                    vBytesPerSecond={vBytesPerSecond}
                    recentBlocks={recentBlocks}
                    diamondbackData={diamondbackData}
                    blockMetrics={blockMetrics}
                  />
              }
            />

            {['/papertrader', '/calculator'].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <CalculatorRoute
                    mode={path === '/papertrader' ? 'trade' : 'btc'}
                    data={data}
                    bitcoinPrice={bitcoinPrice}
                  >
                    <AppContent
                      data={data}
                      bitcoinPrice={bitcoinPrice}
                      lastMempoolBlockData={lastMempoolBlockData}
                      conversions={conversions}
                      da={da}
                      fees={fees}
                      mempoolBlocks={mempoolBlocks}
                      blocks={blocks}
                      mempoolInfo={mempoolInfo}
                      transactions={transactions}
                      vBytesPerSecond={vBytesPerSecond}
                      recentBlocks={recentBlocks}
                      diamondbackData={diamondbackData}
                      blockMetrics={blockMetrics}
                    />
                  </CalculatorRoute>
                }
              />
            ))}

            <Route
              path="/custom"
              element={
                <CustomDashboard
                  data={data}
                  bitcoinPrice={bitcoinPrice}
                  lastMempoolBlockData={lastMempoolBlockData}
                  conversions={conversions}
                  da={da}
                  fees={fees}
                  mempoolBlocks={mempoolBlocks}
                  blocks={blocks}
                  mempoolInfo={mempoolInfo}
                  transactions={transactions}
                  vBytesPerSecond={vBytesPerSecond}
                  animate={animateLastBlock}
                  recentBlocks={recentBlocks}
                  diamondbackData={diamondbackData}
                  blockMetrics={blockMetrics}
                />
              }
            />

          </Routes>
          <ScrollToTopButton />
          {AI_INSIGHTS_FEATURE_ENABLED ? (
            <AIInsightsButton
              data={data}
              bitcoinPrice={bitcoinPrice}
              lastMempoolBlockData={lastMempoolBlockData}
              fees={fees}
              da={da}
              mempoolInfo={mempoolInfo}
              diamondbackData={diamondbackData}
              blockMetrics={blockMetrics}
            />
          ) : null}
          <PWAUpdateNotification />
          <AppFooter
            apiStatus={apiStatus}
            dataMetadata={dataMetadata}
          />
        </MasterWrap>
        </>
      </Router>
      </MetricsProvider>
    </GlossaryProvider>
    </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;